import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Amount } from '../../../web/src/components/display/amount.jsx';
import { useSiteLang } from '../layout/use-site-lang.js';
import { netWorthData, getCurrencyInfo } from './report-utils.js';
import { getBaseCurrency } from './mock-data/mock-data.js';

/**
 * Net Worth report card — mirrors the real app screen markup, driven by the
 * same net-worth logic and the app's Amount component.
 * @returns {JSX.Element}
 */
export function NetWorthCard() {
	const { t } = useTranslation();
	const lang = useSiteLang();
	const base = getBaseCurrency(lang);
	const data = useMemo(() => netWorthData(lang), [lang]);

	/**
	 * @param {number} amount
	 * @param {string} code
	 * @returns {import('react').ReactNode}
	 */
	const amt = (amount, code) => {
		const info = getCurrencyInfo(code, lang);
		if (!info) { return String(amount); }
		return <Amount value={amount} decimalPlaces={info.decimalPlaces} symbol={info.symbol} />;
	};

	const hasPortfolio = data.assets.some((a) => a.principal !== null);

	return (
		<div className="card report-card">
			<div className="card-header"><h2>{t('report.netWorth')}</h2></div>
			<div className="card-body report-body">
				<div className="report-section">
					<h3>{t('report.total')}</h3>
					<div className="report-total">{amt(data.netWorth, base)}</div>
				</div>
				<div className="report-section">
					<h3>{t('report.assets')}</h3>
					<div className="report-table-wrap"><table className="report-table">
						<thead>
							<tr>
								<th>{t('report.colType')}</th>
								<th>{t('report.colCurrency')}</th>
								<th className="right">{t('report.colBalance')}</th>
								<th className="right">{t('report.colConverted')}</th>
							</tr>
						</thead>
						<tbody>
							{data.assetGroups.map((g) => (
								<tr key={g.accountType + g.currency}>
									<td>{t('option.' + g.accountType)}</td>
									<td>{g.currency}</td>
									<td className="right">{amt(g.totalBalance, g.currency)}</td>
									<td className="right">{amt(g.totalConverted, base)}</td>
								</tr>
							))}
							<tr className="report-subtotal">
								<td colSpan={3}>{t('report.assetsSubtotal')}</td>
								<td className="right">{amt(data.assetSum, base)}</td>
							</tr>
							{hasPortfolio && (
								<>
									<tr className="report-muted">
										<td colSpan={3}>{t('report.portfolioCostBasis')}</td>
										<td className="right">{amt(data.totalPrincipal, base)}</td>
									</tr>
									<tr className={data.totalGain >= 0 ? 'report-profit' : 'report-muted'}>
										<td colSpan={3}>{t('report.unrealisedGainLoss')}</td>
										<td className="right">{amt(data.totalGain, base)}</td>
									</tr>
								</>
							)}
						</tbody>
					</table></div>
				</div>
				<div className="report-section">
					<h3>{t('report.liabilities')}</h3>
					<div className="report-table-wrap"><table className="report-table">
						<thead>
							<tr>
								<th>{t('report.colType')}</th>
								<th>{t('report.colCurrency')}</th>
								<th className="right">{t('report.colBalance')}</th>
								<th className="right">{t('report.colConverted')}</th>
							</tr>
						</thead>
						<tbody>
							{data.liabilityGroups.map((g) => (
								<tr key={g.accountType + g.currency}>
									<td>{t('option.' + g.accountType)}</td>
									<td>{g.currency}</td>
									<td className="right">{amt(g.totalBalance, g.currency)}</td>
									<td className="right">{amt(g.totalConverted, base)}</td>
								</tr>
							))}
							<tr className="report-subtotal">
								<td colSpan={3}>{t('report.liabilitiesSubtotal')}</td>
								<td className="right">{amt(data.liabilitySum, base)}</td>
							</tr>
						</tbody>
					</table></div>
				</div>
			</div>
		</div>
	);
}
